import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AutoCompleteModule, AutoComplete } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { DDpApiService } from '../../services/ddpApi.service';
import { SearchWord } from '../../model/SearchWord';
import { APIRequestResponse } from '../../model/RequestResponse';
import { DDPEnglishWords } from '../../model/DDPEnglishWords';
import { DdpDataStore } from '../../services/ddpDataStore.service';
import { LocalStorageService } from '../../services/local-storage.service';

@Component({
  selector: 'ddp-search-bar',
  imports: [CommonModule, AutoCompleteModule, FormsModule],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.css'
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @ViewChild('autoComplete') autoComplete!: AutoComplete;
  
  minimumSearchLength: number = 2;
  maximumSearchLength: number = 25;
  illegalCharactersPattern = /[^a-zA-Z0-9\s']/g;
  results: {id: any, word: string}[] = [];
  isLoading = false;
  isSearchingSuggestion = false;
  request? : APIRequestResponse<string, number, number, number, DDPEnglishWords>;

  constructor(
    private localStorage: LocalStorageService,
    private ddpApiService: DDpApiService,
    public ddpDataStore: DdpDataStore,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Initialize allResults array from localStorage or fetch from API
    var jsonArray = this.localStorage.getItem('autoCompleteList');
    var todaysDate = this.localStorage.getItem('autoCompleteList_savedDate');
    
    if (jsonArray) {
      this.ddpDataStore.allResults = JSON.parse(jsonArray);
    }

    if (!this.ddpDataStore.allResults || !todaysDate || todaysDate != new Date().toISOString().split('T')[0]) {
      this.ddpDataStore.isLoadingList = true;
      this.ddpApiService.GetWordsList().then((data) => {
        this.ddpDataStore.isLoadingList = false;
        this.ddpDataStore.allResults = data;
        this.localStorage.setItem(
          'autoCompleteList',
          JSON.stringify(this.ddpDataStore.allResults)
        );
        const today = new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"
        this.localStorage.setItem('autoCompleteList_savedDate', today);
        this.ddpDataStore.autoCompletionLoaded = true;
      });
    } else {
      this.ddpDataStore.isLoadingList = false;
    }
  }

  ngOnDestroy(): void {
    // Cleanup logic if needed
  }

  onShowOverlay() { }

  filterIllegalCharacters(event: any) {
    //in this method illegal characters are not allowed 
    //unless minimium characters length is reached
    //no white aspaces and single cort in start of text
    const cleanedInput = event.target.value
                          .replace(this.illegalCharactersPattern, '')
                          .replace(/^[\s']+/, '');
    this.ddpDataStore.text = cleanedInput;
  }

  cleanInput(input: string): string {//no white aspaces and single cort in start of text
    //return input.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');
    return input.replace(/^[^a-zA-Z0-9']+|[^a-zA-Z0-9']+$/g, '').replace(/^[\s']+/, '');
  }

  search(event: { query: any, originalEvent: any }) {
    //event.query = this.cleanInput(event.query);
    this.ddpDataStore.text = event.query;
    this.ddpDataStore.query = this.cleanInput(this.ddpDataStore.text);
    if(!this.ddpDataStore.query || this.ddpDataStore?.query?.length < this.minimumSearchLength){
      this.results = [];
      return;
    }
    setTimeout(() => {
      this.isLoading = false;
      //this.ddpDataStore.allResults.map(item => item.word?.replace("-", " "));

      const query = this.ddpDataStore.query.toLowerCase();

      // this.results = this.ddpDataStore.allResults
      //   .filter((item: { word: any }) => 
      //     //item.word.toLowerCase().startsWith(query) //get the words those starts with searhced word
      //    item.word.toLowerCase().includes(query) //get the words those include with searhced word
      //   )
      //   .map((item: { word: any }) => item.word);


      this.results = this.ddpDataStore.allResults
        .filter((item: { word: any }) => item.word.toLowerCase().includes(query)) // Filter words that include the query
        .sort((a: { word: any }, b: { word: any }) => a.word.localeCompare(b.word)) // Sort words alphabetically
        .map((item: { word: any, id: any }) => ({ id: item.id, word: item.word }));

      // Move the word that starts with the query to the first index, if it exists
      const exactMatchIndex = this.results.findIndex((item: { word: string }) => item.word.toLowerCase().startsWith(query.toLowerCase()));
      if (exactMatchIndex > -1) {
        const [exactMatch] = this.results.splice(exactMatchIndex, 1); // Remove the exact match from its current position
        this.results.unshift(exactMatch); // Add the exact match to the beginning of the array
      }


      this.results = this.results.sort((a, b) => {
        if (a.word.toLowerCase() === event.query) return -1;
        if (b.word.toLowerCase() === event.query) return 1;
        return a.word.toLowerCase().indexOf(event.query) - b.word.toLowerCase().indexOf(event.query);
      });
      
      this.ddpDataStore.isSuggestionFound = this.results.length > 0 ? true : false;
      
      // Force show the dropdown if we have results
      if (this.results.length > 0 && this.autoComplete) {
        setTimeout(() => {
          this.autoComplete.show();
        }, 0);
      }

    }, 10);
  }
  
  highlightMatch(word: string, query: string): string {
    const regex = new RegExp(`(${query})`, 'gi');
    const highlightedWord = word.replace(regex, '<span class="ui-autocomplete-highlight">$1</span>');
    query = this.cleanInput(query);

    if(word.toLowerCase() == query.toLowerCase()){
      const firstRippleElement = document.querySelector('.p-ripple');
        if (firstRippleElement) {
            firstRippleElement.classList.add('p-highlight');
        }
    } else {
      // const selector = `.p-ripple[aria-label="${word}"]`;
      // let element = document.querySelector(selector);

      // if (element != null) { // Check if element is not null or undefined
      //   element.classList.remove('p-highlight');
      // } 

      const firstRippleElement = document.querySelector('.p-ripple');
        if (firstRippleElement) {
          const ariaLabel = firstRippleElement.getAttribute('aria-label');
          if(ariaLabel == word.toLowerCase()){
            firstRippleElement.classList.remove('p-highlight');
          }
        }
    }
    return highlightedWord;
  }

  searchOnClick(event: any) {
    //if enter key pressed = 13  or mouse button clicked then go forward 
    if( (this.ddpDataStore.text.trim() == ""  &&  event?.type == "click") || 
        (event.currentTarget.value.trim() == ""  &&  event?.type != "click") ||
        (event?.keyCode != 13 && event?.type != "click")){
      return;
    }
    //if(!this.ddpDataStore.isSuggestionFound) return;
    this.ddpDataStore.text = this.cleanInput(this.ddpDataStore.text);
    var selectedItem = this.ddpDataStore.allResults
    .filter((item: { word: any }) => item.word == this.ddpDataStore.text);
    //console.log(selectedItem);
    
    // if(selectedItem.length == 0){ 
    //   this.ddpDataStore.isSuggestionFound = false; 
    //   return;
    // }


    //The following code first checks the search word is equal to
    //any word in the list then it will directly go to its detail pagem
    //if that word did not match with any word then the search result with a list is shown, 
    //the searc result contains the words those includes the search word 
    //if the list or a single word did not exist then not word found design will be shown

    let searchedWord = this.ddpDataStore.allResults
        .filter((item: { word: any }) => item.word == this.ddpDataStore.text)
        .length;
        
    if(searchedWord == 1){
      this.router.navigate(['worddetail', selectedItem[0].id, 'editthisdetail']);
    } else {
      if(!this.ddpDataStore.isOnList) {
        //this.router.navigate(['searchResult']);
        this.router.navigate(['worddetail', 0, 'editthisdetail']);

      } else {
        this.ddpDataStore.paginatedSearchedAgain.next(true);
      }
    }
  }

  onSelectEvent(event: any) {
    // event now contains the full object with {id, word}
    if (event && event.id && event.word) {
      // Store the selected item in ddpDataStore for access in other components
      this.ddpDataStore.selectedItem = event;
      
      // Update the text field to show only the word
      this.ddpDataStore.text = event.word;
      
      // Navigate using the selected item's id
      this.router.navigate(['worddetail', event.id]);
    } else {
      // Fallback to original logic if event doesn't have expected structure
      var selectedItem = this.ddpDataStore.allResults
      .filter((item: { word: any }) => item.word == this.ddpDataStore.text);

      let searchedWord = this.ddpDataStore.allResults
          .filter((item: { word: any }) => item.word == this.ddpDataStore.text)
          .length;
          
      if(searchedWord == 1){
        this.router.navigate(['worddetail', selectedItem[0].id]);
      } else {
        if(!this.ddpDataStore.isOnList) {
          this.router.navigate(['searchResult']);
        } else {
          this.ddpDataStore.paginatedSearchedAgain.next(true);
        }
      }
    }
  }
  
  onFocusEvent(event: any) {
    this.isLoading = true;
    if(this.ddpDataStore.text != ""){
      setTimeout(() => {
        this.isLoading = false;
        const query = this.ddpDataStore.text.toLowerCase();
        this.results = this.ddpDataStore.allResults
          .filter((item: { word: any }) => item.word.toLowerCase().includes(query))
          .sort((a: { word: any }, b: { word: any }) => a.word.localeCompare(b.word))
          .map((item: { word: any, id: any }) => ({ id: item.id, word: item.word }));
        
        // Move exact match to the top
        const exactMatchIndex = this.results.findIndex((item: { word: string }) => item.word.toLowerCase().startsWith(query));
        if (exactMatchIndex > -1) {
          const [exactMatch] = this.results.splice(exactMatchIndex, 1);
          this.results.unshift(exactMatch);
        }
        
        this.ddpDataStore.isSuggestionFound = this.results.length > 0 ? true : false;
        
        // Force show the dropdown if we have results
        if (this.results.length > 0 && this.autoComplete) {
          setTimeout(() => {
            this.autoComplete.show();
          }, 0);
        }
      }, 10);
    } else {
      this.isLoading = false;
    }
  }

  onKeyUpEvent(event: any) {
    if (event.keyCode == 13) {
      if (this.ddpDataStore.text.length >= 2 && this.ddpDataStore.isSuggestionFound) {
        this.searchOnClick(event);
        this.isLoading = true;
      } else {
        this.isLoading = false;
      }
    } else {
      // For any other key, trigger search to show suggestions
      if (this.ddpDataStore.text && this.ddpDataStore.text.length >= this.minimumSearchLength) {
        this.triggerSuggestions();
      }
    }
  }

  triggerSuggestions() {
    // Manually trigger the search method to show suggestions
    const fakeEvent = { query: this.ddpDataStore.text, originalEvent: null };
    this.search(fakeEvent);
  }
}
